// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
/**
 * LETTORE BUSTA PAGA — Deployment 12
 *
 * GDPR-compliant calcolatore client-side al 100%.
 * - Nessun campo personale (no nome, no cognome, no CF, no IBAN, no datore di lavoro).
 * - Nessuna chiamata di rete: tutti i calcoli sono effettuati nel browser.
 * - Solo input anonimi e numerici (RAL, CCNL, Livello, Mensilità, Anzianità, Figli, Regione).
 *
 * Riferimenti normativi 2026:
 *  - IRPEF: art. 11 D.P.R. 917/86 (TUIR), scaglioni 23% / 33% / 43%.
 *  - Contributi INPS dipendente: 9,19% (in alcuni settori 9,49%).
 *  - Detrazione lavoro dipendente: art. 13 TUIR.
 *  - Addizionali regionali e comunali IRPEF: stima media 2026.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, BookOpen, Info } from 'lucide-react';
import { EducBox, ResultCard, InputRow, fmt, calcIRPEF } from './shared';
import { useDeepLinkParams, hasAllParams, ShareCalcButton, scrollToResult } from './deepLink';

// ── Tabelle CCNL principali (lordo/anno per livello, indicativo) ────────────
const CCNL_TABLE = {
  commercio:   { label: 'Commercio e Terziario', mensilita: 13, livelli: { '7': 18500, '6': 21000, '5': 23800, '4': 26500, '3': 29500, '2': 33500, '1': 38000 } },
  metalmecc:   { label: 'Metalmeccanici Industria', mensilita: 14, livelli: { '7': 20500, '6': 23200, '5': 26100, '4': 28800, '3': 31800, '2': 35500, '1': 41000 } },
  edilizia:    { label: 'Edilizia Industria', mensilita: 13, livelli: { '4': 22500, '3': 25400, '2': 28800, '1': 32500 } },
  pubblico:    { label: 'Funzioni Centrali (P.A.)', mensilita: 13, livelli: { 'C': 22500, 'B': 26500, 'A': 31500 } },
  scuola:      { label: 'Scuola', mensilita: 13, livelli: { 'docente': 27000, 'collaboratore': 21500 } },
  turismo:     { label: 'Turismo / Pubblici esercizi', mensilita: 14, livelli: { '6': 19200, '5': 21500, '4': 24200, '3': 27200, '2': 30500, '1': 34500 } },
  altro:       { label: 'Altro / Non specificato', mensilita: 13, livelli: { 'standard': 0 } },
};

// ── Aliquote regionali medie 2026 (IRPEF) ────────────────────────────────────
const ADDIZIONALE_REGIONALE = {
  Lombardia: 1.23, Lazio: 1.73, Campania: 2.03, Sicilia: 1.23, Veneto: 1.23,
  'Emilia-Romagna': 1.43, Piemonte: 1.62, Puglia: 1.33, Toscana: 1.42,
  Calabria: 1.83, Liguria: 1.63, Sardegna: 1.23, Marche: 1.23, Abruzzo: 1.73,
  Umbria: 1.43, Molise: 2.03, Basilicata: 1.73, 'Friuli-Venezia Giulia': 0.7,
  'Trentino-Alto Adige': 0.7, "Valle d'Aosta": 1.23,
};

// ── Detrazione lavoro dipendente 2026 (art. 13 TUIR) ─────────────────────────
const detrazioneLavoroDipendente = (RAL) => {
  if (RAL <= 15000) return 1955;
  if (RAL <= 28000) return 1910 + 1190 * (28000 - RAL) / 13000;
  if (RAL <= 50000) return 1910 * (50000 - RAL) / 22000;
  return 0;
};

// ── Detrazione figli a carico (Assegno Unico ha sostituito le vecchie detrazioni < 21 anni) ──
const detrazioneFigli = (numFigli, RAL) => {
  // Per figli >21 anni studenti universitari, detrazione semplificata.
  // Stima conservativa: €950 per figlio scalato col reddito.
  if (numFigli <= 0) return 0;
  const base = 950 * numFigli;
  const scale = Math.max(0, 1 - Math.max(0, RAL - 30000) / 65000);
  return base * scale;
};

const LettoreBustaPaga = () => {
  // Deep-link wiring (Deployment 13): ?ral=...&figli=...
  const url = useDeepLinkParams({ ral: 'num', figli: 'int' });

  // Tutti i campi sono anonimi e numerici/categoriali. ZERO dati personali.
  const [RAL, setRAL]               = useState(url.ral ?? '30000');
  const [ccnl, setCcnl]             = useState('commercio');
  const [livello, setLivello]       = useState('3');
  const [mensilitaCustom, setMens]  = useState('');
  const [anzianita, setAnz]         = useState('5');
  const [figli, setFigli]           = useState(url.figli ?? '0');
  const [regione, setRegione]       = useState('Lombardia');

  useEffect(() => {
    if (hasAllParams(url, ['ral', 'figli'])) scrollToResult('lettore-results');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ccnlData = CCNL_TABLE[ccnl] || CCNL_TABLE.commercio;
  const mensilita = parseInt(mensilitaCustom) || ccnlData.mensilita;

  const stipendioSuggerito = ccnlData.livelli[livello] ?? null;

  const calc = useMemo(() => {
    const ralN = parseFloat(RAL) || 0;
    if (ralN <= 0) return null;

    // Contributi INPS dipendente — 9,19%
    const contributiINPS = ralN * 0.0919;

    // Imponibile fiscale = RAL − contributi INPS
    const imponibile = Math.max(0, ralN - contributiINPS);

    // IRPEF lorda (scaglioni 2026)
    const irpefLorda = calcIRPEF(imponibile);

    // Detrazione lavoro dipendente
    const detrLavoro = detrazioneLavoroDipendente(ralN);

    // Detrazione figli (>21 anni, sotto 21 c'è Assegno Unico INPS)
    const detrFigli = detrazioneFigli(parseInt(figli) || 0, ralN);

    // IRPEF netta
    const irpefNetta = Math.max(0, irpefLorda - detrLavoro - detrFigli);

    // Addizionali regionali + comunali (stima media 0,8%)
    const addRegPct = ADDIZIONALE_REGIONALE[regione] || 1.5;
    const addRegionale = imponibile * (addRegPct / 100);
    const addComunale = imponibile * 0.008;

    // Netto annuo
    const nettoAnnuo = ralN - contributiINPS - irpefNetta - addRegionale - addComunale;

    // Netto mensile in busta
    const nettoMensile = nettoAnnuo / mensilita;

    // TFR mensile accantonato (1/13.5 RAL)
    const tfrMensile = ralN / 13.5 / 12;

    return {
      ral: ralN,
      contributiINPS,
      imponibile,
      irpefLorda,
      detrLavoro,
      detrFigli,
      irpefNetta,
      addRegionale,
      addComunale,
      nettoAnnuo,
      nettoMensile,
      tfrMensile,
      cuneoFiscale: ralN > 0 ? ((ralN - nettoAnnuo) / ralN) * 100 : 0,
    };
  }, [RAL, regione, figli, mensilita]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="lettore-busta-paga-calc">
      {/* GDPR PRIVACY NOTICE — visible & prominent */}
      <div className="mb-5 p-4 rounded-xl flex items-start gap-3" style={{ background: 'rgba(61,122,82,0.08)', border: '1px solid rgba(61,122,82,0.3)' }} data-testid="lettore-privacy-notice">
        <ShieldCheck className="w-5 h-5 text-[#3D7A52] flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold text-[#3D7A52] mb-1">100% privato — GDPR compliant</p>
          <p className="text-[#888780] leading-relaxed">
            Tutti i calcoli sono effettuati <strong className="text-[#E8E8E4]">interamente nel tuo browser</strong>: nessun dato lascia mai il tuo dispositivo.
            Non chiediamo né salviamo dati personali (nome, cognome, codice fiscale, IBAN, datore di lavoro). Ricarica la pagina e tutto sparisce.
          </p>
        </div>
      </div>

      <EducBox text="Inserisci i dati anonimi della tua busta paga (RAL, CCNL, livello, regione) per ottenere una stima dello stipendio netto mensile, IRPEF, contributi INPS, addizionali regionali/comunali e TFR accantonato. Nessun dato viene inviato online: tutto resta sul tuo dispositivo." />

      {/* INPUT GRID — only anonymous fields */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <InputRow label="RAL — Retribuzione Annua Lorda (€)" value={RAL} onChange={setRAL} prefix="€" tooltip="La retribuzione annua lorda totale (compreso 13ª/14ª)" />

        <div>
          <label className="text-[#888780] text-sm flex items-center gap-1 mb-1">
            CCNL applicato
            <span title="Contratto Collettivo Nazionale di Lavoro" className="opacity-50"><Info className="w-3 h-3" /></span>
          </label>
          <select value={ccnl} onChange={e => { setCcnl(e.target.value); setLivello(Object.keys(CCNL_TABLE[e.target.value].livelli)[0]); }}
            className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm" data-testid="lettore-ccnl">
            {Object.entries(CCNL_TABLE).map(([id, c]) => <option key={id} value={id}>{c.label}</option>)}
          </select>
        </div>

        <div>
          <label className="text-[#888780] text-sm block mb-1">Livello contrattuale</label>
          <select value={livello} onChange={e => setLivello(e.target.value)}
            className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm" data-testid="lettore-livello">
            {Object.keys(ccnlData.livelli).map(lv => (
              <option key={lv} value={lv}>{`Livello ${lv}${ccnlData.livelli[lv] > 0 ? ` (~€${fmt(ccnlData.livelli[lv])} lordi/anno)` : ''}`}</option>
            ))}
          </select>
        </div>

        <InputRow label="Mensilità (default da CCNL)" value={mensilitaCustom} onChange={setMens} suffix={`def. ${ccnlData.mensilita}`} tooltip="Lascia vuoto per usare il default del CCNL (13 o 14)" />

        <InputRow label="Anzianità (anni)" value={anzianita} onChange={setAnz} suffix="anni" tooltip="Anni di anzianità nel medesimo CCNL — utilizzato solo per scopi di stima" />

        <InputRow label="Figli a carico (>21 anni)" value={figli} onChange={setFigli} suffix="figli" tooltip="Figli a carico fiscale (per under 21 c'è l'Assegno Unico INPS)" />

        <div>
          <label className="text-[#888780] text-sm block mb-1">Regione di residenza fiscale</label>
          <select value={regione} onChange={e => setRegione(e.target.value)}
            className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm" data-testid="lettore-regione">
            {Object.keys(ADDIZIONALE_REGIONALE).map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      {stipendioSuggerito > 0 && Math.abs(parseFloat(RAL) - stipendioSuggerito) / stipendioSuggerito > 0.20 && (
        <div className="mb-4 p-3 rounded-lg text-xs" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', color: '#C9A84C' }}>
          ℹ La RAL inserita (€{fmt(parseFloat(RAL) || 0)}) si discosta significativamente dal valore di riferimento del livello selezionato (~€{fmt(stipendioSuggerito)}). Verifica il livello contrattuale.
        </div>
      )}

      {/* RESULTS */}
      {calc && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-testid="lettore-results">
            <ResultCard label="Netto mensile in busta" value={`€${fmt(calc.nettoMensile)}`} green />
            <ResultCard label="Netto annuo" value={`€${fmt(calc.nettoAnnuo)}`} gold />
            <ResultCard label="TFR mensile accantonato" value={`€${fmt(calc.tfrMensile)}`} sub="1/13,5 della RAL" />
            <ResultCard label="Cuneo fiscale" value={`${calc.cuneoFiscale.toFixed(1)}%`} sub="Tasse + INPS / RAL" />
          </div>

          {/* DETAILS */}
          <div className="rounded-xl overflow-hidden mb-5" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="p-3 text-xs font-medium text-[#888780]" style={{ background: 'rgba(255,255,255,0.03)' }}>
              Dettaglio del calcolo (annuale)
            </div>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-white/[0.04]">
                  <td className="py-2.5 px-3 text-[#888780]">Retribuzione Annua Lorda (RAL)</td>
                  <td className="py-2.5 px-3 text-right text-[#E8E8E4] font-medium">€{fmt(calc.ral)}</td>
                </tr>
                <tr className="border-b border-white/[0.04]">
                  <td className="py-2.5 px-3 text-[#888780]">− Contributi INPS dipendente (9,19%)</td>
                  <td className="py-2.5 px-3 text-right text-[#E8E8E4]">−€{fmt(calc.contributiINPS)}</td>
                </tr>
                <tr className="border-b border-white/[0.04]">
                  <td className="py-2.5 px-3 text-[#888780]">= Imponibile fiscale</td>
                  <td className="py-2.5 px-3 text-right text-[#E8E8E4] font-medium">€{fmt(calc.imponibile)}</td>
                </tr>
                <tr className="border-b border-white/[0.04]">
                  <td className="py-2.5 px-3 text-[#888780]">IRPEF lorda (scaglioni 23/33/43%)</td>
                  <td className="py-2.5 px-3 text-right text-[#E8E8E4]">€{fmt(calc.irpefLorda)}</td>
                </tr>
                <tr className="border-b border-white/[0.04]">
                  <td className="py-2.5 px-3 text-[#888780]">− Detrazione lavoro dipendente (art. 13 TUIR)</td>
                  <td className="py-2.5 px-3 text-right text-[#3D7A52]">−€{fmt(calc.detrLavoro)}</td>
                </tr>
                {calc.detrFigli > 0 && (
                  <tr className="border-b border-white/[0.04]">
                    <td className="py-2.5 px-3 text-[#888780]">− Detrazione figli a carico</td>
                    <td className="py-2.5 px-3 text-right text-[#3D7A52]">−€{fmt(calc.detrFigli)}</td>
                  </tr>
                )}
                <tr className="border-b border-white/[0.04]">
                  <td className="py-2.5 px-3 text-[#888780]">= IRPEF netta annua</td>
                  <td className="py-2.5 px-3 text-right text-[#E8E8E4] font-medium">€{fmt(calc.irpefNetta)}</td>
                </tr>
                <tr className="border-b border-white/[0.04]">
                  <td className="py-2.5 px-3 text-[#888780]">Addizionale regionale ({regione})</td>
                  <td className="py-2.5 px-3 text-right text-[#E8E8E4]">€{fmt(calc.addRegionale)}</td>
                </tr>
                <tr className="border-b border-white/[0.04]">
                  <td className="py-2.5 px-3 text-[#888780]">Addizionale comunale (stima 0,8%)</td>
                  <td className="py-2.5 px-3 text-right text-[#E8E8E4]">€{fmt(calc.addComunale)}</td>
                </tr>
                <tr style={{ background: 'rgba(61,122,82,0.08)' }}>
                  <td className="py-3 px-3 text-[#3D7A52] font-bold">= Netto annuo</td>
                  <td className="py-3 px-3 text-right text-[#3D7A52] font-bold">€{fmt(calc.nettoAnnuo)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Cross-link to GuidaBustaPaga (text guide) */}
      <ShareCalcButton slug="busta-paga" testid="lettore-share-btn"
        getValues={() => ({ ral: RAL, figli })} />

      <Link to="/guida-busta-paga" className="block p-4 rounded-xl text-sm transition-colors hover:bg-white/[0.04] mt-4"
        style={{ background: '#252523', border: '1px solid rgba(201,168,76,0.25)' }}
        data-testid="lettore-link-guida">
        <div className="flex items-center gap-3">
          <BookOpen className="w-5 h-5 text-[#C9A84C] flex-shrink-0" />
          <div>
            <p className="font-semibold text-[#E8E8E4]">Vuoi capire ogni voce della busta paga?</p>
            <p className="text-xs text-[#888780] mt-0.5">Consulta la <span className="text-[#C9A84C]">Guida interattiva alla Busta Paga</span> — versione testuale che spiega tutte le sezioni (testa, corpo, piede, conguaglio).</p>
          </div>
        </div>
      </Link>
</motion.div>
  );
};

export default LettoreBustaPaga;
